import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Grid2 as Grid, Typography, FormControl, FormGroup, InputLabel } from '@mui/material';
import { withStyles } from '@mui/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import { useController } from 'react-hook-form-v7';
import { textFieldStyles } from '../../../Configurations/ExpenseEntryConfig';
import { COLORS, DATE_FORMATS } from '../../../../utils/Application_Constants';

const styles = (theme) => {
    const baseStyles = textFieldStyles(theme);
    return {
        datePicker: {
            width: '10em',
            ...baseStyles.root,
            '& .MuiIconButton-root': {
                color: COLORS.LT_JOURNEY,
            },
        },
    };
};

const dateInputSlotProps = {
    popper: { sx: { zIndex: 1320 } },
    dialog: { sx: { zIndex: 1320 } },
    textField: {
        placeholder: 'mm/dd/yyyy',
        variant: 'standard',
        margin: 'none',
        InputLabelProps: { shrink: true },
        InputProps: {
            sx: { flexShrink: 1 },
            disableUnderline: true,
        },
        sx: { '& .MuiPickersInputBase-root': { mt: 1 } },
    },
    openPickerButton: { 'aria-label': 'Choose date' },
};

export const getDateRangePickerInputNames = (name) => {
    const inputNameFrom = `${name}.from`;
    const inputNameTo = `${name}.to`;
    return { inputNameFrom, inputNameTo };
};

const ConnectedDateRangePicker = forwardRef(({ control, classes, name }, ref) => {
    const [fromFocused, setFromFocused] = useState(false);
    const [toFocused, setToFocused] = useState(false);

    const { inputNameFrom, inputNameTo } = getDateRangePickerInputNames(name);

    const {
        field: { value: rangeFrom, onChange: setFromDate },
    } = useController({ control, name: inputNameFrom, defaultValue: null });
    const {
        field: { value: rangeTo, onChange: setToDate },
    } = useController({ control, name: inputNameTo, defaultValue: null });

    const [dateError, setDateError] = useState('');

    const handleClear = () => {
        setFromDate(null);
        setToDate(null);
        setDateError('');
    };

    useImperativeHandle(ref, () => ({
        clear: handleClear,
    }));

    const handleDateChange = (date, type, setValue) => {
        setDateError('');

        if (date) {
            const newDate = moment(date).format(DATE_FORMATS.ISO_8601);
            setValue(newDate);
        } else {
            setValue(null);
        }

        if (date) {
            if (type === inputNameFrom && rangeTo && moment(date).isAfter(moment(rangeTo))) {
                setDateError('Start date cannot be greater than end date');
            } else if (
                type === inputNameTo &&
                rangeFrom &&
                moment(date).isBefore(moment(rangeFrom))
            ) {
                setDateError('End date cannot be less than start date');
            }
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <FormControl component="fieldset" ref={ref}>
                <FormGroup>
                    <Grid container spacing={2}>
                        <Grid size={6}>
                            <InputLabel shrink focused={fromFocused} htmlFor={inputNameFrom}>
                                From
                            </InputLabel>
                            <DatePicker
                                name={inputNameFrom}
                                data-testid={`${inputNameFrom}-input`}
                                value={rangeFrom ? moment(rangeFrom) : null}
                                onChange={(d) => handleDateChange(d, inputNameFrom, setFromDate)}
                                className={classes.datePicker}
                                slotProps={{
                                    ...dateInputSlotProps,
                                    textField: {
                                        ...dateInputSlotProps.textField,
                                        id: inputNameFrom,
                                        onFocus: () => setFromFocused(true),
                                        onBlur: () => setFromFocused(false),
                                    },
                                    popper: {
                                        ...dateInputSlotProps.popper,
                                        onFocus: () => setFromFocused(true),
                                        onBlur: () => setFromFocused(false),
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={6}>
                            <InputLabel shrink focused={toFocused} htmlFor={inputNameTo}>
                                To
                            </InputLabel>
                            <DatePicker
                                name={inputNameTo}
                                data-testid={`${inputNameTo}-input`}
                                value={rangeTo ? moment(rangeTo) : null}
                                onChange={(d) => handleDateChange(d, inputNameTo, setToDate)}
                                className={classes.datePicker}
                                slotProps={{
                                    ...dateInputSlotProps,
                                    textField: {
                                        ...dateInputSlotProps.textField,
                                        id: inputNameTo,
                                        onFocus: () => setToFocused(true),
                                        onBlur: () => setToFocused(false),
                                    },
                                    popper: {
                                        ...dateInputSlotProps.popper,
                                        onFocus: () => setToFocused(true),
                                        onBlur: () => setToFocused(false),
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                    {dateError && <Typography color="error">{dateError}</Typography>}
                </FormGroup>
            </FormControl>
        </LocalizationProvider>
    );
});

export default withStyles(styles)(ConnectedDateRangePicker);
